import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormatNumberPipe } from '../../../pipes/format.number.pipe';
import { QuoteSuccessMessageComponent, QuoteSuccessData } from '../quote-success-message/quote-success-message.component';
import { 
  CostBreakdown, 
  PartDetail,
  QuantityPricing,
  CostItem,
  CostSection,
  ProfitSection,
  QuoteDetails
} from '../../quote-assist-v2.model';
import {
  CostBreakdownChangeEvent,
  ComponentValidationState,
  ComponentConfig,
  CostCalculationEvent,
  CostCategoryUpdateEvent
} from '../../models/component-events.model';

@Component({
  selector: 'app-cost-breakdown',
  standalone: true,
  imports: [CommonModule, FormsModule, FormatNumberPipe, QuoteSuccessMessageComponent],
  templateUrl: './cost-breakdown.component.html',
  styleUrls: ['./cost-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostBreakdownComponent implements OnInit, OnDestroy, OnChanges {
  @Input() costBreakdown!: CostBreakdown;
  @Input() parts: PartDetail[] = [];
  // @Input() partsPricingData: PartDetail[] = [];
  @Input() quoteDetails!: QuoteDetails;
  @Input() config!: ComponentConfig;
  @Input() validationState!: ComponentValidationState;
  @Input() isQuoteSubmitted: boolean = false;
  @Input() contactId?: number; // Added for future functionality like PDF downloads

  @Output() costBreakdownChange = new EventEmitter<CostBreakdownChangeEvent>();
  @Output() stepNavigation = new EventEmitter<any>();
  @Output() partEdit = new EventEmitter<{ partIndex: number }>();

  /** Max character limit for Notes to Buyer field */
  readonly buyerNotesMaxLength = 1000;

  // Local state
  public localCostBreakdown: CostBreakdown = this.initializeCostBreakdown();
  public activeBuyerNotesIndex: number | null = null;
  public notesTooltipPartIndex: number | null = null;
  public notesTooltipPosition: { left: number; top: number } = { left: 0, top: 0 };
  private validationErrors: { [fieldName: string]: string } = {};
  private buyerNotesErrors: { [partIndex: number]: string } = {};
  private buyerNotesExpanded: { [partIndex: number]: boolean } = {};
  private subscriptions: Subscription[] = [];
  showHelp: boolean = false;
  
  // Success message state
  public showSuccessMessage: boolean = false;
  public successMessageData!: QuoteSuccessData;

  // Helper for template string indexing
  public getSection(key: string): CostSection {
    return (this.localCostBreakdown as any)[key];
  }

  // Cost categories
  readonly costCategories = [
    { key: 'materials', label: 'Materials', description: 'Raw materials and components' },
    { key: 'labor', label: 'Labor', description: 'Direct labor and assembly' },
    { key: 'overhead', label: 'Overhead', description: 'Indirect costs and facilities' },
    { key: 'tooling', label: 'Tooling', description: 'Jigs, fixtures, and tooling' },
    { key: 'otherCosts', label: 'Other Costs', description: 'Additional expenses' }
  ];

  // Validation rules
  private readonly validationRules: { [key: string]: any } = {
    materials: { required: false, minValue: 0 },
    labor: { required: false, minValue: 0 },
    overhead: { required: false, minValue: 0 },
    tooling: { required: false, minValue: 0 },
    otherCosts: { required: false, minValue: 0 },
    profit: { required: true, minValue: 0, maxValue: 100 }
  };

  ngOnInit(): void {
    this.initializeComponent();
    
    // Initialize success message data if quote is already submitted
    if (this.isQuoteSubmitted) {
      this.initializeSuccessMessageData();
      // Don't show success message if component is re-initialized (user returning)
      this.showSuccessMessage = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to isQuoteSubmitted input
    if (changes['isQuoteSubmitted'] && changes['isQuoteSubmitted'].currentValue) {
      this.showSuccessMessage = true;
      this.initializeSuccessMessageData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // ===== INITIALIZATION =====

  private initializeComponent(): void {
    this.localCostBreakdown = { ...this.costBreakdown };
    this.calculateTotalCosts();
    this.validateAllFields();
  }

  private initializeCostBreakdown(): CostBreakdown {
    return {
      materials: { totalCost: 0, items: [] },
      tooling: { totalCost: 0, items: [] },
      manufacturing: { totalCost: 0, items: [] },
      labor: { totalCost: 0, items: [] },
      overhead: { totalCost: 0, items: [] },
      otherCosts: { totalCost: 0, items: [] },
      profit: { profitMargin: 0, totalProfit: 0 }
    };
  }

  // ===== COST CALCULATIONS =====

  private calculateTotalCosts(): void {
    // Calculate total cost from parts
    const partsTotalCost = this.calculatePartsTotalCost();
    
    // Calculate total cost from all sections
    const totalCost = partsTotalCost + 
      this.localCostBreakdown.materials.totalCost +
      this.localCostBreakdown.labor.totalCost +
      this.localCostBreakdown.overhead.totalCost +
      this.localCostBreakdown.tooling.totalCost +
      this.localCostBreakdown.otherCosts.totalCost;

    // Update manufacturing section with parts cost
    this.localCostBreakdown.manufacturing.totalCost = partsTotalCost;

    // Calculate profit
    this.calculateProfit(totalCost);

    this.emitCostCalculation();
  }

  private calculatePartsTotalCost(): number {
    return this.parts.reduce((total, part) => {
      const partTotal = part.quantityPricing.reduce((partTotal, pricing) => {
        return partTotal + (pricing.total || 0);
      }, 0);
      return total + partTotal;
    }, 0);
  }

  private calculateProfit(totalCost: number): void {
    const profitMargin = this.localCostBreakdown.profit.profitMargin;
    this.localCostBreakdown.profit.totalProfit = totalCost * (profitMargin / 100);
  }

  // ===== FIELD CHANGE HANDLERS =====

  onCostFieldChange(categoryKey: string, value: number): void {
    const section = this.localCostBreakdown[categoryKey as keyof CostBreakdown] as CostSection;
    if (section) {
      section.totalCost = value;
    }
    
    const event: CostCategoryUpdateEvent = {
      category: categoryKey,
      value,
      isValid: this.validateField(categoryKey, value),
      error: this.getFieldError(categoryKey)
    };

    this.updateValidationState(event);
    this.calculateTotalCosts();
    this.emitCostBreakdownChange();
  }

  onProfitMarginChange(value: number): void {
    this.localCostBreakdown.profit.profitMargin = value;
    this.calculateTotalCosts();
    this.validateField('profit', value);
    this.emitCostBreakdownChange();
  }

  // ===== VALIDATION =====

  private validateField(fieldName: string, value: number): boolean {
    const rules = this.validationRules[fieldName];
    if (!rules) return true;

    let isValid = true;
    let errorMessage = '';

    // Required validation
    if (rules.required && (value === null || value === undefined || value < 0)) {
      isValid = false;
      errorMessage = `${this.getFieldDisplayName(fieldName)} is required`;
    }

    // Min value validation
    if (isValid && rules.minValue !== undefined && value < rules.minValue) {
      isValid = false;
      errorMessage = `${this.getFieldDisplayName(fieldName)} must be at least ${rules.minValue}`;
    }

    // Max value validation
    if (isValid && rules.maxValue !== undefined && value > rules.maxValue) {
      isValid = false;
      errorMessage = `${this.getFieldDisplayName(fieldName)} cannot exceed ${rules.maxValue}`;
    }

    // Update validation state
    if (isValid) {
      delete this.validationErrors[fieldName];
    } else {
      this.validationErrors[fieldName] = errorMessage;
    }

    return isValid;
  }

  private validateAllFields(): void {
    Object.keys(this.validationRules).forEach(fieldName => {
      if (fieldName === 'profit') {
        this.validateField(fieldName, this.localCostBreakdown.profit.profitMargin);
      } else {
        const section = this.localCostBreakdown[fieldName as keyof CostBreakdown] as CostSection;
        if (section) {
          this.validateField(fieldName, section.totalCost);
        }
      }
    });
    
    // Validate buyer notes for all parts
    this.validateAllBuyerNotes();
  }

  validate(): boolean {
    this.validateAllFields();
    return Object.keys(this.validationErrors).length === 0;
  }

  // ===== UTILITY METHODS =====

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      materials: 'Materials',
      labor: 'Labor',
      overhead: 'Overhead',
      tooling: 'Tooling',
      otherCosts: 'Other Costs',
      profit: 'Profit Margin'
    };
    return displayNames[fieldName] || fieldName;
  }

  private updateValidationState(event: CostCategoryUpdateEvent): void {
    // Integrate with centralized validation handling when available.
  }

  private emitCostBreakdownChange(): void {
    const isValid = this.isFormValid();
    const stepCompleted = this.isStepCompleted();

    const event: CostBreakdownChangeEvent = {
      costBreakdown: { ...this.localCostBreakdown },
      isValid,
      errors: { ...this.validationErrors },
      stepCompleted
    };

    this.costBreakdownChange.emit(event);
  }

  private emitCostCalculation(): void {
    const totalCost = this.getTotalCost();
    const event: CostCalculationEvent = {
      section: 'manufacturing',
      totalCost,
      items: [],
      calculationMethod: 'manual',
      timestamp: new Date().toISOString()
    };

    // Hook for analytics or further aggregation can be added here.
  }

  private isFormValid(): boolean {
    const costFieldsValid = Object.keys(this.validationErrors).length === 0;
    const buyerNotesValid = Object.keys(this.buyerNotesErrors).length === 0;
    return costFieldsValid && buyerNotesValid;
  }

  private isStepCompleted(): boolean {
    const totalCost = this.getTotalCost();
    return totalCost > 0 && 
           this.localCostBreakdown.profit.profitMargin >= 0 &&
           !this.validationErrors['profit'];
  }

  // ===== PUBLIC METHODS =====

  getFieldClass(fieldName: string): string {
    const hasError = this.validationErrors[fieldName];
    
    let classes = 'form-control';
    
    if (hasError) {
      classes += ' error-border';
    }
    
    if (this.config.readonly) {
      classes += ' readonly';
    }
    
    return classes;
  }

  getFieldError(fieldName: string): string | undefined {
    return this.validationErrors[fieldName];
  }

  isFieldRequired(fieldName: string): boolean {
    return this.validationRules[fieldName]?.required || false;
  }

  getTotalCost(): number {
    return this.localCostBreakdown.materials.totalCost +
           this.localCostBreakdown.labor.totalCost +
           this.localCostBreakdown.overhead.totalCost +
           this.localCostBreakdown.tooling.totalCost +
           this.localCostBreakdown.otherCosts.totalCost +
           this.localCostBreakdown.manufacturing.totalCost;
  }

  getTotalPrice(): number {
    const totalCost = this.getTotalCost();
    const profitAmount = this.localCostBreakdown.profit.totalProfit;
    return totalCost + profitAmount;
  }

  getCostPercentage(costValue: number): number {
    const totalCost = this.getTotalCost();
    if (totalCost === 0) return 0;
    return (costValue / totalCost) * 100;
  }

  getPartsTotalCost(): number {
    return this.calculatePartsTotalCost();
  }

  getCostBreakdownData(): Array<{label: string, value: number, color: string}> {
    const partsCost = this.getPartsTotalCost();
    const profitAmount = this.localCostBreakdown.profit.totalProfit;

    return [
      { label: 'Parts Cost', value: partsCost, color: '#2E58D6' },
      { label: 'Materials', value: this.localCostBreakdown.materials.totalCost, color: '#28a745' },
      { label: 'Labor', value: this.localCostBreakdown.labor.totalCost, color: '#ffc107' },
      { label: 'Overhead', value: this.localCostBreakdown.overhead.totalCost, color: '#17a2b8' },
      { label: 'Tooling', value: this.localCostBreakdown.tooling.totalCost, color: '#6f42c1' },
      { label: 'Other Costs', value: this.localCostBreakdown.otherCosts.totalCost, color: '#fd7e14' },
      { label: 'Profit', value: profitAmount, color: '#dc3545' }
    ].filter(item => item.value > 0);
  }

  // ===== TRACKBY FUNCTIONS =====

  trackByCategoryKey(index: number, category: {key: string, label: string, description: string}): string {
    return category.key;
  }

  trackByPartIndex(index: number, part: PartDetail): number {
    return index;
  }

  trackByPricingIndex(index: number, pricing: QuantityPricing): number {
    return pricing.quantity || 0;
  }

  // ===== UTILITY METHODS =====

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  // Part calculation methods for template
  getPartTotalQuantity(part: PartDetail): number {
    return part.quantityPricing.reduce((sum, pricing) => sum + (pricing.quantity || 0), 0);
  }

  getPartUnitCost(part: PartDetail): number {
    return part.quantityPricing.length > 0 ? (part.quantityPricing[0].unitCost || 0) : 0;
  }

  getPartTotalCost(part: PartDetail): number {
    return part.quantityPricing.reduce((sum, pricing) => sum + (pricing.total || 0), 0);
  }

  // ===== PART EDIT HANDLERS =====

  onEditPart(partIndex: number): void {
    // Keep sidebar part selection in sync immediately from costs view.
    if (this.parts && this.parts.length > 0) {
      this.parts.forEach((part, index) => {
        part.isActivePart = index === partIndex;
      });
    }
    
    this.partEdit.emit({ partIndex });
  }

  // ===== BUYER NOTES HANDLERS =====

  onBuyerNotesChange(partIndex: number, value: string): void {
    // Update the part's notesForBuyer property
    if (this.parts && this.parts[partIndex]) {
      this.parts[partIndex].notesForBuyer = value;
    }
    
    // Also update the main parts array to keep them in sync
    if (this.parts && this.parts[partIndex]) {
      this.parts[partIndex].notesForBuyer = value;
    }
    
    // Validate the buyer notes
    this.validateBuyerNotes(partIndex, value);
    
    // Emit changes to parent component
    this.emitCostBreakdownChange();
  }

  openBuyerNotesModal(partIndex: number): void {
    this.activeBuyerNotesIndex = partIndex;
  }

  showNotesTooltip(partIndex: number, event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tooltipWidth = 190;
    const gap = 8;
    const tooltipHeight = 44;
    this.notesTooltipPartIndex = partIndex;
    this.notesTooltipPosition = {
      left: rect.left - tooltipWidth - gap,
      top: rect.top + rect.height / 2 - tooltipHeight / 2
    };
  }

  hideNotesTooltip(): void {
    this.notesTooltipPartIndex = null;
  }

  closeBuyerNotesModal(): void {
    this.activeBuyerNotesIndex = null;
  }

  isBuyerNotesExpanded(partIndex: number): boolean {
    const expanded = this.buyerNotesExpanded[partIndex];
    return expanded === undefined ? false : expanded;
  }

  toggleBuyerNotes(partIndex: number): void {
    const current = this.isBuyerNotesExpanded(partIndex);
    this.buyerNotesExpanded[partIndex] = !current;
  }

  private validateBuyerNotes(partIndex: number, value: string): boolean {
    let isValid = true;
    let errorMessage = '';

    // Check if value exceeds max length
    if (value && value.length > this.buyerNotesMaxLength) {
      isValid = false;
      errorMessage = `Buyer notes cannot exceed ${this.buyerNotesMaxLength} characters`;
    }

    // Update validation state
    if (isValid) {
      delete this.buyerNotesErrors[partIndex];
    } else {
      this.buyerNotesErrors[partIndex] = errorMessage;
    }

    return isValid;
  }

  getBuyerNotesError(partIndex: number): string | undefined {
    return this.buyerNotesErrors[partIndex];
  }

  validateAllBuyerNotes(): boolean {
    let allValid = true;
    
    if (this.parts) {
      this.parts.forEach((part, index) => {
        if (!this.validateBuyerNotes(index, part.notesForBuyer || '')) {
          allValid = false;
        }
      });
    }
    
    return allValid;
  }

  // Initialize success message data when quote is submitted
  private initializeSuccessMessageData(): void {
    this.successMessageData = {
      companyName: this.quoteDetails.companyName
    };
  }
} 